import { Check, Moon, Pipette, Sun } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "../../design-system/cn.js";
import { Panel } from "../../design-system/surfaces.js";
import {
  APPROVED_ACCENTS,
  isHexColor,
  type AccentId,
  type ThemeMode,
} from "../../design-system/theme.js";
import { useTheme } from "../../design-system/theme-provider.js";
import { PageHeading } from "../shared/page-heading.js";

/**
 * Two choices. Nothing here is a preference the product could have decided for the
 * listener: mode and accent are taste, and taste is his.
 */
export function SettingsPage() {
  const { accent, customAccent, mode, setAccent, setCustomAccent, setMode } =
    useTheme();

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeading subtitle="How the app looks." title="Appearance" />

      <Section title="Theme">
        <Field label="Mode">
          <div className="inline-flex rounded-sm border border-border bg-bg-2 p-0.5">
            {MODES.map((option) => (
              <button
                key={option.id}
                aria-pressed={mode === option.id}
                className={cn(
                  "inline-flex min-h-9 items-center gap-2 rounded-[3px] px-3.5 text-sm font-medium transition-colors",
                  mode === option.id
                    ? "bg-surface text-ink shadow-sm"
                    : "text-ink-3 hover:text-ink-2",
                )}
                onClick={() => {
                  setMode(option.id);
                }}
                type="button"
              >
                <option.icon aria-hidden="true" className="size-4" />
                {option.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Accent" note="Sets the accent colour across the app.">
          <div className="flex flex-wrap items-center gap-2.5">
            {(Object.keys(APPROVED_ACCENTS) as AccentId[]).map((id) => (
              <Swatch
                key={id}
                color={APPROVED_ACCENTS[id].value}
                label={APPROVED_ACCENTS[id].label}
                onSelect={() => {
                  setAccent(id);
                }}
                selected={accent === id}
              />
            ))}
            <CustomAccent
              onSelect={setCustomAccent}
              selected={accent === "custom"}
              value={customAccent}
            />
          </div>
        </Field>
      </Section>
    </div>
  );
}

const MODES: { id: ThemeMode; label: string; icon: typeof Sun }[] = [
  { id: "dark", label: "Dark", icon: Moon },
  { id: "light", label: "Light", icon: Sun },
];

function Swatch({
  color,
  label,
  onSelect,
  selected,
}: {
  color: string;
  label: string;
  onSelect: () => void;
  selected: boolean;
}) {
  return (
    <button
      aria-label={label}
      aria-pressed={selected}
      className={cn(
        "grid size-9 place-items-center rounded-full border transition-[box-shadow,border-color]",
        selected
          ? "border-ink/20 shadow-[0_0_0_2px_var(--bg),0_0_0_4px_var(--accent-ring)]"
          : "border-black/10 hover:shadow-[0_0_0_2px_var(--bg),0_0_0_4px_var(--border-hi)]",
      )}
      onClick={onSelect}
      style={{ background: color }}
      title={label}
      type="button"
    >
      {selected ? (
        // The check only ever sits on the selected swatch, whose colour is the
        // current accent — so the accent's own ink is exactly the right contrast.
        <Check aria-hidden="true" className="size-4 text-accent-ink" />
      ) : null}
    </button>
  );
}

function CustomAccent({
  onSelect,
  selected,
  value,
}: {
  onSelect: (hex: string) => void;
  selected: boolean;
  value: string;
}) {
  const [draft, setDraft] = useState(value);
  const valid = isHexColor(draft);

  return (
    <div className="flex items-center gap-2">
      <label
        className={cn(
          "relative grid size-9 cursor-pointer place-items-center rounded-full border transition-[box-shadow,border-color]",
          selected
            ? "border-ink/20 shadow-[0_0_0_2px_var(--bg),0_0_0_4px_var(--accent-ring)]"
            : "border-black/10 hover:shadow-[0_0_0_2px_var(--bg),0_0_0_4px_var(--border-hi)]",
        )}
        style={{ background: value }}
        title="Custom accent"
      >
        <input
          aria-label="Custom accent colour"
          className="absolute inset-0 cursor-pointer opacity-0"
          onChange={(event) => {
            setDraft(event.target.value);
            onSelect(event.target.value);
          }}
          type="color"
          value={value}
        />
        {selected ? (
          <Check aria-hidden="true" className="size-4 text-accent-ink" />
        ) : (
          // Unselected, this would be a seventh colour with no meaning of its own.
          // The pipette says what it is: a colour the listener picks.
          <Pipette aria-hidden="true" className="size-4 text-black/60" />
        )}
      </label>
      <input
        aria-label="Custom accent hex"
        className={cn(
          "h-9 w-28 rounded-sm border bg-bg-2 px-2.5 font-mono text-xs text-ink outline-none",
          valid ? "border-border" : "border-err",
        )}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (isHexColor(next)) onSelect(next);
        }}
        placeholder="#f0a35a"
        spellCheck={false}
        value={draft}
      />
    </div>
  );
}

function Section({ children, title }: { children: ReactNode; title: string }) {
  return (
    <Panel className="p-5">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      <div className="mt-4 flex flex-col gap-5">{children}</div>
    </Panel>
  );
}

function Field({
  children,
  label,
  note,
}: {
  children: ReactNode;
  label: string;
  note?: string;
}) {
  return (
    <div>
      <div className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase">
        {label}
      </div>
      <div className="mt-2">{children}</div>
      {note ? (
        <p className="mt-2 text-xs leading-5 text-ink-3">{note}</p>
      ) : null}
    </div>
  );
}
